import { reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import React from 'react';
import { FB, FBWorkspaceModeOptions } from '..';
import { lhrHasNoSerialNumbers } from '../FBAffectedSerialNumber/utils';
import {
  DEVIATION_SELECTED_LHRT_KEY,
  DEVIATION_SERIAL_NUMBERS_KEY,
  DeviationSerialNumberEntry,
  DeviationSerialNumbers,
  FBAffectedLhrSelectionProps,
} from './FBAffectedLhrSelection.types';

export const withFBAffectedLhrSelection = <T extends FBAffectedLhrSelectionProps>(
  Component: React.FunctionComponent<T>,
) => {
  const Comp = ({
    disabled,
    isOutput,
    name = '',
    ...props
  }: T) => {
    const { workspaceState, formState } = FB.useStores();
    const { PREVIEW, FORMPREVIEW } = FBWorkspaceModeOptions;

    isOutput = workspaceState?.isOutput;
    const isPreviewMode = workspaceState?.mode ? [PREVIEW, FORMPREVIEW].includes(workspaceState.mode as FBWorkspaceModeOptions) : false;
    disabled = isPreviewMode || !isOutput;

    const urlValues = useObserver(() =>
      workspaceState?.document?.formInput?.[DEVIATION_SELECTED_LHRT_KEY]
      || workspaceState?.formInputSync.get(DEVIATION_SELECTED_LHRT_KEY),
    );

    React.useEffect(() => reaction(
      () => formState?.inputState.get(name)?.value as string[] | undefined,
      (selectedIds) => {
        // LHRs without serial numbers have nothing to pick in Affected Serial Numbers
        const ids: string[] = (Array.isArray(selectedIds) ? selectedIds : []).filter((id) => !lhrHasNoSerialNumbers(id));
        const existing = (formState?.getFieldValue(DEVIATION_SERIAL_NUMBERS_KEY) ?? []) as DeviationSerialNumbers;
        const existingIds = new Set(existing.map((entry) => Object.keys(entry)[0]));

        const updated: DeviationSerialNumbers = ids.map((id) => {
          if (existingIds.has(id)) {
            return existing.find((entry) => Object.keys(entry)[0] === id) as Record<string, DeviationSerialNumberEntry>;
          }
          return { [id]: { whereUsed: false, serialNumbers: [] } };
        });

        formState?.setFieldValue(DEVIATION_SERIAL_NUMBERS_KEY, updated);
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), []);

    return Component({
      ...(props as T),
      isOutput,
      disabled,
      name,
      urlValues,
    });
  };

  Comp.displayName = 'withFBAffectedLhrSelection';
  return Comp;
};
